import type { SiteData } from './types';

/** Seeded content, lifted verbatim from the v2 handoff (data.js).
 * The data layer uses this as the fallback when Supabase is not configured
 * or queries return empty. Edit here for pre-CMS content changes. */
export const staticData: SiteData = {
  companies: [
    { name: 'Bokksu', exit: 'Acq. by Hashi Brands', role: 'Global eComm & CPG innovator', year: '2024', tag: 'CPG · Food' },
    { name: 'Mesmer', exit: 'Failed · Funded by Intel / True', role: 'Pioneered AI-powered software testing', year: '2020', tag: 'AI · Dev tools' },
    { name: 'Element', exit: 'Co-founded w/ Yan La Cunn', role: 'Pioneered digital identity in the developing world', year: '2018', tag: 'Identity' },
    { name: 'F#', exit: 'Acq. by Frequency', role: 'Pioneered digital music advertising', year: '2016', tag: 'AdTech' },
    { name: 'Kno', exit: 'Acq. by Intel', role: 'Pioneered interactive textbooks', year: '2013', tag: 'EdTech' },
    { name: 'Zenprise', exit: 'Acq. by Citrix', role: 'Pioneered mobile device security', year: '2013', tag: 'Security' },
    { name: 'EDS', exit: 'Acq. by HP', role: 'Revolutionized global IT delivery', year: '2008', tag: 'Infra' },
    { name: 'Loudcloud', exit: 'Acq. by EDS', role: 'Pioneered cloud computing', year: '2007', tag: 'Cloud' },
    { name: 'Affinia', exit: 'Failed · Funded by Sequoia', role: 'Pioneered contextual ads', year: '2001', tag: 'AdTech' },
  ],
  now: [
    { body: "Eating my way through Tokyo's neighborhood yakitori counters" },
    { body: 'Building an AI layer for cross-cultural CPG' },
    { body: 'Reading: The Art of the Japanese Garden' },
    { body: 'Advising 4 early-stage founders' },
    { body: 'Chasing untracked powder between advisory calls' },
    { body: 'Training for a sub-4 marathon (fueled by grapefruit)' },
  ],
  writing: [
    { slug: 'acquired-founders-paradox',   date: '2026 · 04', title: "The acquired founder's paradox",          kicker: 'Nine exits taught me this',    minutes: 7 },
    { slug: 'cpg-operators-not-brands',    date: '2026 · 03', title: 'Why CPG needs operators, not just brands', kicker: 'Notes from Bokksu',            minutes: 9 },
    { slug: 'tokyo-taste-long-arc',        date: '2026 · 02', title: 'Tokyo, taste, and the long arc of a snack', kicker: 'On bridging cultures',         minutes: 12 },
    { slug: 'ai-shipping-problem',         date: '2026 · 01', title: 'AI will not save your shipping problem',   kicker: 'What Mesmer actually learned', minutes: 6 },
    { slug: 'quiet-math-early-customers',  date: '2025 · 11', title: 'The quiet math of early customers',        kicker: '0→1 is a listening game',      minutes: 5 },
  ],
  circuit: [
    { when_label: 'MAY 14', where: 'SaaStr Annual',    city: 'San Mateo', talk: 'Nine ventures, one working theory' },
    { when_label: 'JUN 03', where: 'Lisbon AI Summit', city: 'Lisbon',    talk: 'Bridging cultures through models' },
    { when_label: 'SEP 22', where: 'Web Summit Tokyo', city: 'Tokyo',     talk: "The operator's case for taste" },
    { when_label: 'NOV 11', where: 'Slush',            city: 'Helsinki',  talk: 'Listening as product strategy' },
  ],
  yakitori: [
    { n: '01', name: 'Toriki',         hood: 'Meguro, TYO' },
    { n: '02', name: 'Bird Land',      hood: 'Ginza, TYO' },
    { n: '03', name: 'Torishiki',      hood: 'Meguro, TYO' },
    { n: '04', name: 'Yakitori Totto', hood: 'Midtown, NYC' },
    { n: '05', name: 'Torien',         hood: 'West Village, NYC' },
  ],
};

/** Fallback dispatch shown when Supabase isn't configured or the table is
 * empty. The admin manages live dispatches at /admin/dispatch — when one is
 * published, getCurrentDispatch returns it and this fallback is hidden. */
export const staticDispatch = {
  issue_label: 'Vol. XXV · Issue 02 · MMXXVI',
  filed_location: 'Steamboat Springs (SBS)',
  read_minutes: 3,
  body_md: `I stopped writing about ten years ago. Not on purpose. The pace of building crowded it out, and the list I'd promise to write to never got worked through.

Picking it up again because the work itself just changed.

More on the site once Supabase is configured.`,
  body_html: '',
  signoff_html:
    'More of this here as I figure it out - cheers,<br>_d<br>From Creekside Cafe in Steamboat Springs (SBS)',
  published: true,
};

/** Static profile fields — things unlikely to churn, or that we want hard-coded
 * until an admin/settings table exists. */
export const profile = {
  name: 'Dan Merritts',
  tagline: 'Company builder, operator, investor.',
  intro: 'Bridging cultures through food and AI-innovation.',
  residences: ['TYO', 'SBS', 'NYC'],
  since: 2001,
  countries: 101,
  stats: [
    { label: 'Ventures',  value: '09' },
    { label: 'Exits',     value: '07' },
    { label: 'Years',     value: '25' },
    { label: 'Countries', value: '101' },
  ] as const,
  // Ordered west-to-east by TZ offset (so they read in the order the day
  // moves across Dan's map): Tokyo, San Francisco, Denver, New York.
  cities: [
    { code: 'TYO', tz: 'Asia/Tokyo' },
    { code: 'SFO', tz: 'America/Los_Angeles' },
    { code: 'DEN', tz: 'America/Denver' },
    { code: 'NYC', tz: 'America/New_York' },
  ] as const,
  email: 'questions@danmerritts.com',
  portrait: { src: '/assets/dan-portrait.jpg', coords: '35.67 N · 139.65 E' },
} as const;
